import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  ALL_AVAILABLE_SUBJECT_IDS, 
  getSubjectClass, 
  getSubjectName, 
} from '../../utils/constants';

const TeacherSubjects = () => {
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  
  const isAdmin = userProfile?.role === 'admin';
  const assigned = userProfile?.assignedSubjects || [];
  const subjects = (isAdmin || assigned.length === 0) ? ALL_AVAILABLE_SUBJECT_IDS : assigned;

  const [selectedClass, setSelectedClass] = useState('ALL');
  const [searchQuery, setSearchQuery] = useState('');

  const classList = ['ALL', 'JSS1', 'JSS2', 'JSS3', 'SS1', 'SS2', 'SS3', 'UTME'];

  const filteredSubjects = subjects.filter((subject) => {
    const kClass = getSubjectClass(subject).toUpperCase();
    const name = getSubjectName(subject).toLowerCase();
    const queryMatch = name.includes(searchQuery.toLowerCase()) || subject.toLowerCase().includes(searchQuery.toLowerCase());
    const classMatch = selectedClass === 'ALL' || kClass === selectedClass;
    return queryMatch && classMatch;
  });

  return (
    <div className="max-w-7xl mx-auto w-full py-2 md:py-4">
      
      {/* Page Header (No icons, human typography, standard professional sizes) */}
      <div className="mb-6 md:mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-extrabold text-darkNavy mb-1.5 font-poppins tracking-tight">
            {isAdmin ? 'All Academic Subjects' : 'My Assigned Subjects'}
          </h1>
          <p className="text-xs md:text-sm text-gray-500 font-medium">
            {isAdmin 
              ? 'Select any subject to add lesson curriculum materials or generate quizzes.' 
              : 'Manage lesson plans and quiz materials for your teaching curriculum.'}
          </p>
        </div>

        {/* Action Shortcuts (Clean human buttons, NO icons) */}
        <div className="flex items-center gap-2.5 flex-shrink-0">
          <button
            onClick={() => navigate('/teacher/upload')}
            className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-800 border border-slate-300 rounded-xl text-xs md:text-sm font-bold transition-all cursor-pointer shadow-2xs"
          >
            Upload Lesson
          </button>
          <button
            onClick={() => navigate('/teacher/quiz')}
            className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs md:text-sm font-bold transition-all shadow-xs cursor-pointer"
          >
            Quiz Builder
          </button>
        </div>
      </div>

      {/* Class Level Filters & Search Row (No icons) */}
      <div className="bg-white p-3.5 md:p-4 rounded-2xl border border-gray-200 mb-6 flex flex-col md:flex-row gap-3.5 justify-between items-center shadow-xs">
        
        {/* Class Level Filter Pills */}
        <div className="flex items-center gap-1.5 overflow-x-auto w-full md:w-auto pb-1 md:pb-0 scrollbar-hide">
          {classList.map((cls) => {
            const isActive = selectedClass === cls;
            return (
              <button
                key={cls}
                onClick={() => setSelectedClass(cls)}
                className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all whitespace-nowrap cursor-pointer border ${
                  isActive
                    ? 'bg-indigo-600 text-white border-indigo-700 shadow-sm'
                    : 'bg-indigo-50 text-indigo-900 border-indigo-200 hover:bg-indigo-100'
                }`}
              >
                {cls}
              </button>
            );
          })}
        </div>

        {/* Clean Search Input (No icons, standard font size) */}
        <div className="w-full md:w-72">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search subjects by name..."
            className="w-full px-3.5 py-2 bg-gray-50 border border-gray-200 rounded-xl text-xs font-medium text-darkNavy placeholder:text-gray-400 focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-all"
          />
        </div>
      </div>

      {/* Subject Cards Grid */}
      {filteredSubjects.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-dashed border-gray-300 shadow-xs">
          <p className="text-base font-bold text-gray-700 mb-1">No Subjects Found</p>
          <p className="text-xs text-gray-500 max-w-md mx-auto">
            {searchQuery 
              ? 'No subjects matched your search keywords. Try searching for a different term.' 
              : 'There are no subjects assigned to this class category.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {filteredSubjects.map((subject) => {
            const subjectClass = getSubjectClass(subject).toUpperCase();
            const subjectName = getSubjectName(subject);

            return (
              <div 
                key={subject} 
                className="bg-white p-4 md:p-5 rounded-2xl border border-gray-200 shadow-xs hover:shadow-md transition-all flex flex-col justify-between"
              >
                {/* Top Row: Class Badge on Left, "+ Lesson" and "+ Quiz" Buttons on Right (shaded in red) */}
                <div className="flex items-center justify-between gap-2 mb-3">
                  <span className="inline-block text-[11px] font-bold uppercase tracking-wider text-indigo-700 bg-indigo-50 border border-indigo-200 px-2.5 py-0.5 rounded-md flex-shrink-0">
                    {subjectClass}
                  </span>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <button 
                      onClick={() => navigate('/teacher/upload', { state: { defaultSubject: subject } })}
                      className="py-1 px-2.5 bg-slate-100 hover:bg-slate-200 text-slate-800 rounded-lg text-xs font-bold border border-slate-200 transition-colors text-center cursor-pointer shadow-2xs whitespace-nowrap"
                    >
                      + Lesson
                    </button>
                    <button 
                      onClick={() => navigate('/teacher/quiz', { state: { defaultSubject: subject } })}
                      className="py-1 px-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-bold transition-colors text-center cursor-pointer shadow-xs whitespace-nowrap"
                    >
                      + Quiz
                    </button>
                  </div>
                </div>

                {/* Subject Name with Standard Human Font Size */}
                <div>
                  <h3 className="font-bold text-sm md:text-base text-darkNavy leading-snug line-clamp-2">
                    {subjectName}
                  </h3>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TeacherSubjects;
